import {
  effect,
  Injectable,
  makeStateKey,
  signal,
  StateKey,
  TransferState,
} from '@angular/core';
import { IClub } from '../../../../server/interfaces/club';
import { IMatchSession } from '../../../../server/interfaces/match-session';
import { config } from '../../../../shared/config';
import { SportType } from '../../../../shared/constants/sport-type';
import { ILocationCoordinate } from '../../../../shared/interfaces/location';
import { ApiResponse, HttpService } from './http.service';
import { NotificationService } from './notification.service';
import { SportFilterService } from './sport-filter.service';
import { isPlatformServer } from '@angular/common';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root',
})
export class ClubService {
  filter = signal<string>('');
  nearbyClubs = signal<IClub[]>([]);
  gettingNearbyClubs = signal(false);
  cursor = signal<number>(Date.now());

  club = signal<IClub | null>(null);
  gettingClub = signal<boolean>(false);
  endOfClubList = signal(false);

  initialized = signal(false);

  matchSessions = signal<IMatchSession[]>([]);
  gettingMatchSessions = signal(false);
  endOfMatchSessionList = signal(false);

  recurringSessions = signal<IMatchSession[]>([]);
  gettingRecurringSessions = signal(false);

  constructor(
    private http: HttpService,
    private notification: NotificationService,
    private sportFilterService: SportFilterService,
    private transferState: TransferState,
    private platform: PlatformService
  ) {}

  async getMatchSessions(
    clubId: string,
    coordinates?: ILocationCoordinate,
    page = 1
  ) {
    this.matchSessions.set([]);
    this.endOfMatchSessionList.set(false);

    const offset = (page ? page - 1 : 0) * config.dataTake;

    this.gettingMatchSessions.set(true);

    let res;

    const stateKey: StateKey<ApiResponse<IMatchSession[]>> = makeStateKey<
      ApiResponse<IMatchSession[]>
    >('transfer-get-match-sessions');

    // If it's in TransferState (on client), use it and remove from TransferState
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<ApiResponse<IMatchSession[]>>(
        stateKey,
        {} as ApiResponse<IMatchSession[]>
      );
      res = data;
      this.transferState.remove(stateKey);
    } else {
      res = await this.http.get<IMatchSession[]>(
        `/match-session/club-and-coordinates`,
        {
          lat: coordinates?.coordinates[1] ?? '',
          long: coordinates?.coordinates[0] ?? '',
          clubId,
          offset,
          cursor: this.cursor(),
        }
      );
    }
    if (this.platform.isServer()) {
      this.transferState.set(stateKey, res);
    }
    this.gettingMatchSessions.set(false);
    if (res.error) {
      this.notification.error(res.error);
      return;
    }
    if (res.data) {
      this.matchSessions.set(res.data);
      this.endOfMatchSessionList.set(res.data.length < config.dataTake);
    }
  }

  async getRecurringSessions(
    clubId: string,
    coordinates?: ILocationCoordinate
  ) {
    if (this.gettingRecurringSessions()) return;

    this.gettingRecurringSessions.set(true);
    const res = await this.http.get<IMatchSession[]>(
      `/match-session/recurring-by-club/${clubId}`,
      {
        lat: coordinates?.coordinates[1] ?? '',
        long: coordinates?.coordinates[0] ?? '',
      }
    );
    this.gettingRecurringSessions.set(false);

    if (res.error) {
      this.notification.error(res.error);
      return;
    }

    if (res.data) {
      this.recurringSessions.set(res.data);
    } else {
      this.recurringSessions.set([]);
    }
  }

  async getNearbyClubs(
    sportType?: SportType | '',
    coordinates?: ILocationCoordinate,
    page?: number,
    resetCursor = false
  ) {
    this.initialized.set(true);
    // if (this.gettingNearbyClubs()) return;
    // if (!coordinates) return;

    this.nearbyClubs.set([]);
    this.endOfClubList.set(false);

    const stateKey: StateKey<number> = makeStateKey<number>(
      'transfer-get-nearby-clubs-cursor'
    );

    // If it's in TransferState (on client), use it and remove from TransferState
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<number>(stateKey, {} as number);
      this.transferState.remove(stateKey);
      this.cursor.set(data);
    } else if (resetCursor) this.cursor.set(Date.now());

    // Get nearby clubs
    const offset = (page ? page - 1 : 0) * config.dataTake;

    this.gettingNearbyClubs.set(true);
    const res = await this.http.get<IClub[]>(`/club/nearby-by-coordinates`, {
      lat: coordinates?.coordinates[1] ?? '',
      long: coordinates?.coordinates[0] ?? '',
      sportType: sportType ?? this.sportFilterService.filter(),
      offset,
      filter: this.filter(),
      cursor: this.cursor(),
    });
    if (this.platform.isServer()) {
      this.transferState.set(stateKey, this.cursor());
    }
    this.gettingNearbyClubs.set(false);

    if (res.error) {
      this.notification.error(res.error);
      return;
    }

    if (res.data) {
      this.nearbyClubs.set(res.data);
      this.endOfClubList.set(res.data.length < config.dataTake);
    }

    return true;
  }

  async getClub(id: string) {
    this.matchSessions.set([]);
    this.endOfMatchSessionList.set(false);

    this.gettingClub.set(true);

    let res;

    const stateKey: StateKey<ApiResponse<IClub>> = makeStateKey<
      ApiResponse<IClub>
    >(`transfer-get-club-${id}`);

    // If it's in TransferState (on client), use it and remove from TransferState
    if (this.transferState.hasKey(stateKey)) {
      const data = this.transferState.get<ApiResponse<IClub>>(
        stateKey,
        {} as ApiResponse<IClub>
      );
      res = data;
      this.transferState.remove(stateKey);
    } else {
      res = await this.http.get<IClub>(`/club/${id}`, null);
    }

    if (this.platform.isServer()) {
      this.transferState.set(stateKey, res);
    }

    this.gettingClub.set(false);

    if (res.data) {
      this.club.set(res.data);
      return res.data;
    } else {
      this.club.set(null);
      this.notification.error(res.error);
    }

    return null;
  }

}
